import { useState } from 'react';
import type { Item } from '../db/types';
import { colorOf } from '../lib/colors';
import { formatTime, hasTime, monthName, parseMonthKey, thisMonthKey } from '../lib/dates';
import type { CalendarDay, Catalog } from '../lib/views';
import { calendarTotals } from '../lib/views';
import './CalendarPane.css';

interface Props {
  month: string;
  days: CalendarDay[];
  catalog: Catalog;
  selectedId: string | null;
  onShiftMonth: (by: number) => void;
  onGoToMonth: (month: string) => void;
  onOpen: (item: Item) => void;
  /** Compose a note already dated to the day it was started from. */
  onCompose: (day: Date) => void;
  onReschedule: (item: Item, day: Date) => void;
}

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Beyond this a cell would set the row's height for every other cell in it. */
const VISIBLE = 3;

/**
 * A month as a topography of what you wrote.
 *
 * The one idea the whole view rests on: a day with something in it sits on
 * `bg-surface`, an empty day on `bg-base`. That is the same surface stack the
 * rest of the app uses for depth, so the month's rhythm reads at a glance
 * without a single badge, dot or count being added to say it.
 */
export function CalendarPane({
  month,
  days,
  catalog,
  selectedId,
  onShiftMonth,
  onGoToMonth,
  onOpen,
  onCompose,
  onReschedule,
}: Props) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [dragging, setDragging] = useState<Item | null>(null);
  const [over, setOver] = useState<string | null>(null);

  const first = parseMonthKey(month);
  const totals = calendarTotals(days);
  const isCurrentMonth = month === thisMonthKey();

  return (
    <section className="cal" aria-label="Calendar">
      <header className="cal__head">
        {/* Title and count share a line while there is room and stack when the
            editor is open — the month nav never leaves the right edge. */}
        <div className="cal__headText">
          <h1 className="cal__title">
            {monthName(first)} <span className="cal__year">{first.getFullYear()}</span>
          </h1>

          <p className="cal__totals">
            {totals.notes === 0 && totals.tasks === 0 ? (
              'Nothing this month'
            ) : (
              <>
                {totals.notes > 0 && <>{totals.notes} {totals.notes === 1 ? 'note' : 'notes'}</>}
                {totals.notes > 0 && totals.tasks > 0 && <span className="cal__dot"> · </span>}
                {totals.tasks > 0 && <>{totals.tasks} {totals.tasks === 1 ? 'task' : 'tasks'}</>}
              </>
            )}
          </p>
        </div>

        <div className="cal__nav">
          <button
            type="button"
            className="cal__navBtn"
            aria-label="Previous month"
            onClick={() => onShiftMonth(-1)}
          >
            ‹
          </button>
          <button
            type="button"
            className="cal__today"
            disabled={isCurrentMonth}
            onClick={() => onGoToMonth(thisMonthKey())}
          >
            Today
          </button>
          <button
            type="button"
            className="cal__navBtn"
            aria-label="Next month"
            onClick={() => onShiftMonth(1)}
          >
            ›
          </button>
        </div>
      </header>

      <div className="cal__dow" aria-hidden="true">
        {DOW.map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>

      {/* Keyed on the month so a change replays the entrance rather than
          swapping 42 cells in place with no indication anything moved. */}
      <div className="cal__scroll">
        <div className="cal__grid" role="grid" aria-label="Days" key={month}>
          {days.map((day) => {
            const open = expanded === day.key;
            const shown = open ? day.items : day.items.slice(0, VISIBLE);
            const hidden = day.items.length - shown.length;

            return (
              <div
                key={day.key}
                role="gridcell"
                className={[
                  'cal__day',
                  day.items.length > 0 && 'has-items',
                  !day.inMonth && 'is-outside',
                  day.isToday && 'is-today',
                  over === day.key && 'is-over',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onDragOver={(e) => {
                  if (!dragging) return;
                  e.preventDefault();
                  setOver(day.key);
                }}
                onDragLeave={() => setOver((k) => (k === day.key ? null : k))}
                onDrop={() => {
                  if (dragging) onReschedule(dragging, day.date);
                  setDragging(null);
                  setOver(null);
                }}
              >
                <div className="cal__dayHead">
                  <span className="cal__num">{day.date.getDate()}</span>
                  <button
                    type="button"
                    className="cal__add"
                    aria-label={`New note on ${day.date.toDateString()}`}
                    onClick={() => onCompose(day.date)}
                  >
                    +
                  </button>
                </div>

                <div className="cal__items">
                  {shown.map((item) => (
                    <Chip
                      key={item.id}
                      item={item}
                      catalog={catalog}
                      selected={item.id === selectedId}
                      onOpen={onOpen}
                      onDragStart={() => setDragging(item)}
                      onDragEnd={() => {
                        setDragging(null);
                        setOver(null);
                      }}
                    />
                  ))}

                  {hidden > 0 && (
                    <button
                      type="button"
                      className="cal__more"
                      onClick={() => setExpanded(day.key)}
                    >
                      {hidden} more
                    </button>
                  )}
                  {open && day.items.length > VISIBLE && (
                    <button type="button" className="cal__more" onClick={() => setExpanded(null)}>
                      Show less
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ── Chip ────────────────────────────────────────────────── */

function Chip({
  item,
  catalog,
  selected,
  onOpen,
  onDragStart,
  onDragEnd,
}: {
  item: Item;
  catalog: Catalog;
  selected: boolean;
  onOpen: (item: Item) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}) {
  // An open task's priority is the most urgent thing the edge can encode; with
  // no priority set, the list it lives in is the next most useful.
  const priority = item.kind === 'todo' && !item.done && item.priority < 4 ? item.priority : null;
  const listColor = colorOf(catalog.lists.find((l) => l.id === item.list_id)?.color);
  const anchor = item.due_at ?? item.start_at;
  const time = anchor && hasTime(anchor) ? formatTime(anchor) : null;

  return (
    <button
      type="button"
      draggable
      className={[
        'cal__chip',
        priority && `is-p${priority}`,
        item.done && 'is-done',
        selected && 'is-selected',
        item.kind === 'note' && 'is-note',
      ]
        .filter(Boolean)
        .join(' ')}
      style={!priority && listColor ? { borderLeftColor: listColor } : undefined}
      title={item.title || 'Untitled'}
      onClick={() => onOpen(item)}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      {time && <span className="cal__chipTime">{time}</span>}
      <span className="cal__chipTitle">{item.title || 'Untitled'}</span>
    </button>
  );
}
